// dependencies
import { Map, fromJS } from "immutable";
import { GenerateProcess } from "redux-process";

import Config from "./Config";

import { ChangePage, ChangeQueryParameters } from "../../../actions/navigation.actions";
import { DisplayError } from "../../../actions/notifications.actions";
// actions
import { StateChange } from "../../../actions/page.actions";

import { getSignatureTemplate, getSignatureTemplateSteps } from "../../../selectors/documents";
// selectors
import { getWorkflowTemplate } from "../../../selectors/workflows";
import {
	getAllInputsAreValid,
	getCurrentPage,
	getDocumentPicker,
	getFormInputs,
	getLastFetchedWorkflow,
	getSelectionObjectID,
	getTodoLabelsError,
	getTodoObject,
	getTodos,
	getUI,
	getWorkflowData,
} from "./selectors";

// utils
import ShortId from "../../../utils/ShortId";

// config
import { getOptionById } from "../../../constants/EmployeePickerPrepend";
import ui from "./ui";

const LASTPAGE = 3;

export const launchWorkflow = () => async (dispatch, getState) => {
	const state = getState();
	const workflowData = getWorkflowData(state);

	const result = await dispatch(
		GenerateProcess("LaunchWorkflow", {
			payload: {
				workflow: workflowData,
			},
		})
	);

	if (result.succeeded) {
		dispatch(ChangePage(`/jobs/${result.response.get("jobID", "")}/`));
		return true;
	}

	dispatch(DisplayError("An error occurred while launching the workflow"));
	return false;
};

/**
 * Gets a workflow template. Will check the `getLastFetchedWorkflow to make
 * sure the workflow hasn't already been fetched. After we fetch the template,
 * store a copy of it in the page state. That copy will rewrite any actors with
 * a value of 'other' to an empty string. But the user is still able to set the
 * value to other manually.
 */
export const fetchWorkflowTemplate = () => async (dispatch, getState) => {
	const state = getState();
	const workflowTemplateID = getFormInputs(state).getIn(["workflowTemplate", "id"], null);

	if (!workflowTemplateID || getLastFetchedWorkflow(state) === workflowTemplateID) {
		return;
	}

	const result = await dispatch(GenerateProcess("GetWorkflowTemplate", { workflowTemplateID }));

	if (result.succeeded) {
		const updatedState = getState();
		const workflowTemplate = getWorkflowTemplate(updatedState, workflowTemplateID);

		// get current state
		let uiState = getUI(updatedState);

		// create an updated state
		uiState = uiState.withMutations((mutable) => {
			mutable.set("lastFetchedWorkflowTemplate", workflowTemplateID);
			mutable.set("workflowTodos", workflowTemplate.get("todos"));
		});

		// update the store
		dispatch(StateChange(Config.pageName, [], uiState));
	}
};

/**
 * Creates a selection object and adds it to the query parameters.
 */
export const createSelectionObject = () => async (dispatch) => {
	const request = await dispatch(GenerateProcess("SelectionObjectCreate"));

	if (request.succeeded) {
		dispatch(
			ChangeQueryParameters({
				selectionObjectID: request.response.get("selectionObjectID"),
			})
		);
	}
};

/** appends a blank todo to the list */
export const addTodoTemplate = (id) => (dispatch, getState) => {
	const state = getState();
	const todos = getTodos(state);

	if (todos.size >= Config.todosMaxCount) {
		return;
	}

	const emptyTodo = Map({
		daysToComplete: "",
		label: "",
		actor: "",
		documentSigning: null,
		id: !id ? ShortId() : id,
	});

	dispatch(StateChange(Config.pageName, ["workflowTodos"], todos.push(emptyTodo)));
};

/**
 * removes a todo from the list
 * @param {string} todoID - id of the todo to remove
 */
export const removeTodo = (todoID) => (dispatch, getState) => {
	const state = getState();
	const todos = getTodos(state);

	if (todos.size > 1) {
		dispatch(
			StateChange(
				Config.pageName,
				["workflowTodos"],
				todos.filterNot((todo) => todo.get("id") === todoID)
			)
		);
	}
};

/**
 * Moves a todo from one index to another.
 * @param {int} sourceIndex the current index of the todo
 * @param {int} destinationIndex the desired index of the todo
 */
export const reorderTodos = (sourceIndex, destinationIndex) => (dispatch, getState) => {
	const state = getState();
	const todos = getTodos(state);

	if (destinationIndex < 0) {
		return;
	}

	const oldTodo = todos.get(sourceIndex);
	const newTodoList = todos.delete(sourceIndex).insert(destinationIndex, oldTodo);

	dispatch(StateChange(Config.pageName, ["workflowTodos"], newTodoList));
};

/**
 * Changes a value in a todo item. Limits the label length to the value in the
 * config file for `todoNameMaxCharacters`
 * @param {string} value - new value of the key in the todo
 * @param {string} name - the key that we are changing
 * @param {number} index - index of the todo in the list
 */
export const todoValueChange = (val, name, index) => (dispatch) => {
	let value = val;

	if (name === "label") {
		value = value.slice(0, Config.todoNameMaxCharacters);
	}

	dispatch(StateChange(Config.pageName, ["workflowTodos", index, name], value));
};

/**
 * Opens the document picker, sets the value to the todoID
 * @param {string} todoID - id of the todo we are attaching the doument to
 */
export const openDocumentPicker = (todoID) => (dispatch) => {
	dispatch(StateChange(Config.pageName, ["documentPicker"], todoID));
};

/**
 * Sets the documentPicker value to null which closes the documentPicker.
 */
export const closeDocumentPicker = () => (dispatch) => {
	dispatch(StateChange(Config.pageName, ["documentPicker"], null));
};

/**
 * Attaches a document to a todo. In order to do this, we need to fetch the
 * document so we can get information about the document name and all the
 * signature steps
 * @param {string} documentID
 */
export const attachDocumentToTodo = (documentID) => async (dispatch, getState) => {
	let state = getState();
	const todos = getTodos(state);
	const todoID = getDocumentPicker(state);
	const todoIndex = todos.findIndex((todo) => todo.get("id") === todoID);

	// fetch the signature template
	const request = await dispatch(GenerateProcess("GetSignatureTemplate", { templateID: documentID }));
	if (request.succeeded) {
		state = getState();

		const signatureTemplate = getSignatureTemplate(state, documentID);
		let signatureTemplateSteps = getSignatureTemplateSteps(state, documentID);

		signatureTemplateSteps = signatureTemplateSteps.map((step, ii) => {
			const userObject = getOptionById(step.get("signer"));

			return Map({
				index: ii,
				signer: userObject,
			});
		});

		const attachedDocument = fromJS({
			id: documentID,
			name: signatureTemplate.get("name", ""),
			steps: signatureTemplateSteps,
		});

		dispatch(StateChange(Config.pageName, ["workflowTodos", todoIndex, "documentSigning"], attachedDocument));
		dispatch(closeDocumentPicker());
	}
};

/**
 * Updates the signer of a signature step
 * @param {number} todoIndex - index of the todo
 * @param {number} stepIndex - index of the step within the todo
 * @param {string|Map} value - signer value
 */
export const changeSignatureStep = (todoIndex, stepIndex, value) => (dispatch) => {
	dispatch(
		StateChange(
			Config.pageName,
			["workflowTodos", todoIndex, "documentSigning", "steps", stepIndex, "signer"],
			value
		)
	);
};

/**
 * Get a single signature template. This is used for the
 * `EditSignatureTemplate` modal, which just allows a user to view meta data
 * about a document that is attached to a todo. It will also give them a button
 * that will allow them to remove it. If `viewDocumentModal` has a todoID, then
 * the modal will pop up.
 */
export const openEditSignatureTemplateModal = (todoID) => (dispatch, getState) => {
	const state = getState();
	const todo = getTodoObject(state, todoID);
	const documentID = todo.getIn(["documentSigning", "id"]);

	dispatch(StateChange(Config.pageName, ["viewDocumentModal"], todoID));
	dispatch(GenerateProcess("GetSignatureTemplate", { templateID: documentID }));
};

/**
 * Sets the viewDocumentModal value to null which closes the view document
 * modal.
 */
export const closeSignatureTemplateEditor = () => (dispatch) => {
	dispatch(StateChange(Config.pageName, ["viewDocumentModal"], null));
};

/**
 * Removes the document of a todo.
 * @param {string} todoID
 */
export const removeDocumentFromTodo = (todoID) => (dispatch, getState) => {
	const state = getState();
	const todos = getTodos(state);
	const todoIndex = todos.findIndex((todo) => todo.get("id") === todoID);

	dispatch(todoValueChange(null, "documentSigning", todoIndex));
	dispatch(closeSignatureTemplateEditor());
};

/**
 * Updates the current page. If they page changes at any point and there is no
 * selectionObjectID, then create one. The selection object will be added to
 * the query params.
 * @param {number} page - page number to update to
 */
export const changePage = (page) => async (dispatch, getState) => {
	const state = getState();
	const currentPage = getCurrentPage(state);
	const selectionObjectID = getSelectionObjectID(state);

	if (selectionObjectID === null) {
		await dispatch(createSelectionObject());
	}

	// if moving from page 1 to a page greater than 1, check the user count in
	// the selection object before navigating
	if (currentPage === 1 && page > 1) {
		const selectionCountRes = await dispatch(
			GenerateProcess("SelectionObjectCountUsers", {
				id: selectionObjectID,
			})
		);

		if (!selectionCountRes.succeeded || selectionCountRes.response.get("count", 0) === 0) {
			dispatch(DisplayError("You must select at least one person"));
			return;
		}
	}

	// if visiting the last page, do some checks first
	if (page === LASTPAGE) {
		const todos = getTodos(state);
		const inputLabelErrors = getTodoLabelsError(state);
		const allInputsAreValid = getAllInputsAreValid(state);

		// if todos size is 0, then they have not been fetched yet. Redirect
		// the user to the third page to setup their todos.
		if (todos.size === 0) {
			dispatch(StateChange(Config.pageName, ["currentPage"], LASTPAGE - 1));
			return;
		}
		// if any actors/signer have been ignored, redirect them to the third
		// page and display an error.
		else if (!allInputsAreValid) {
			dispatch(DisplayError("All inputs must have a value."));
			dispatch(StateChange(Config.pageName, ["currentPage"], LASTPAGE - 1));
			return;
		}
		// if any labels are over 250 characters, redirect them to the third
		// page
		else if (inputLabelErrors) {
			dispatch(DisplayError(inputLabelErrors));
			dispatch(StateChange(Config.pageName, ["currentPage"], LASTPAGE - 1));
			return;
		}
	}

	dispatch(StateChange(Config.pageName, ["currentPage"], page));
};

/**
 * Updates the value of a form input. If that form input is a selected value
 * from the `workflowTemplate`, then also change the name of the workflow with
 * the name of the selected workflow template.
 *
 * @param {string} name - name of the input we are updating
 * @param {string} value - next value of the input
 */
export const changeFormInput = (name, val) => (dispatch, getState) => {
	let value = null;
	let keyPath = null;

	if (name === "workflowTemplate" && Map.isMap(val)) {
		const state = getState();
		const inputs = getFormInputs(state);

		keyPath = ["inputs"];
		value = inputs.withMutations((mutable) => {
			mutable.set("workflowTemplate", val);
			mutable.set("workflowName", val.get("name", ""));
		});
	} else {
		keyPath = ["inputs", name];
		value = val;
	}

	dispatch(StateChange(Config.pageName, keyPath, value));
};

export const changePageValue = (keyPath, value) => (dispatch) => {
	dispatch(StateChange(Config.pageName, keyPath, value));
};

/**
 * Initializes the UI.
 * This will add columns to the initial state.
 * */
export const initialize = () => (dispatch) => {
	// 1 - load in the initial state
	let initialState = ui;

	// 2 - load in the columns
	const columns = fromJS([
		{ name: "", _id: "_isChecked", type: "_checkBox" },
		{ name: "Name", _id: "_displayName", type: "_displayName" },
	]);

	initialState = initialState.set("columns", columns);

	// 4 - update our state
	dispatch(StateChange(Config.pageName, [], initialState));
};
